import dayjs from "dayjs";
import TableStyled, { TablePDF } from "./styled";
import ShiftCard from "./components/ShiftCard/";
import { ButtonOutline, ButtonSolid } from "component/button";
import DateChanger from "../Shift/components/DateChanger";
import {
  MONTH_FORMAT,
  DATE_FORMAT,
  DATE_ERA_FORMAT_YEAR_MONTH,
} from "constant/date";
import { useEffect, useMemo, useState, useRef } from "react";
import MolalSchedule from "./components/Modal";
import { EMPLOYMENT_TYPE_OPTIONS, ROLE } from "constant";
import {
  SelectedItem,
  ShiftManagement,
  ShiftType,
  TypeDataSubmit,
} from "./type";
import apiFactory from "api";
import { useQuery } from "react-query";
import { Popover, TableColumnsType } from "antd";
import { Shift } from "page/ManageShiftStaff/type";
import { SHIFT_TYPES } from "constant";
import { toast } from "react-toastify";
import { Flex } from "@chakra-ui/react";
import { RootState } from "store";
import { useSelector } from "react-redux";
import { isEmpty, uniqueId } from "lodash";
import useResponsive from "hook/useResponsive";
import usePrint from "hook/usePrint";
import moment from "moment";
import useDaysArray from "./hooks/useDaysArray";
import useShiftSelection from "./hooks/useShiftSelection";


const ManageShiftStaff = ({ role }: { role?: string }) => {
  const { id: nurseId } = useSelector((state: RootState) => state.user);
  const isStaff = role === ROLE.STAFF;
  const isAdmin = role === ROLE.ADMIN;

  const api = isStaff
    ? apiFactory.staffShiftManagementStaffs
    : apiFactory.adminShiftManagementStaffs;

  const { isMobile, isTablet, windowWidth } = useResponsive();

  const [yearMonth, setYearMonth] = useState(dayjs().format(MONTH_FORMAT));
  const [isViewModel, setIsViewModel] = useState(true);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  const [openModal, setOpenModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameNurseSelecting, setNameNurseSelecting] = useState("");
  const [updatingLocked, setUpdatingLocked] = useState(false);

  const [isBlurModePrint, setIsBlurModePrint] = useState(false);

  const [printing, setPrinting] = useState(false);

  const { ref, handlePrint } = usePrint(true, {
    onBeforePrint: () => {
      setPrinting(false);
    },
  });

  const { data, isLoading, isFetching, refetch } = useQuery<{
    shift_lock: boolean;
    data: ShiftManagement[];
  }>({
    queryKey: [yearMonth],
    queryFn: () => api.getList({ year_month: yearMonth }),
    keepPreviousData: true,
  });

  const handleSubmitUpdateStatusLocked = async () => {
    try {
      setUpdatingLocked(true);
      await apiFactory.adminShiftManagementStaffs.updateLocked({
        year_month: yearMonth,
        status: locked ? "available" : "shift_lock",
      });
      await refetch();
      toast.success("更新に成功しました。");
    } catch (e) {
      toast.error("保存が失敗しました。");
    } finally {
      setUpdatingLocked(false);
    }
  };

  const [preDataSubmit, setPreDataSubmit] = useState<TypeDataSubmit>({});

  const daysArray = useDaysArray(yearMonth);

  const dataSource = useMemo(() => {
    if (isStaff) {
      // prepare for data staff
      const result: any = [];
      data?.data?.forEach((item) => {
        const { id, family_name, shifts, nurse_code, employment_type } = item; // id = nurseId
        //if (!shifts && id !== userId) return;

        const dataSubmitArray = preDataSubmit[id]
          ? Object.values(preDataSubmit[id])
          : [];
        const shiftsData: {
          [key: string]: Pick<
            Shift,
            "shift_type" | "date" | "nurse_id" | "note"
          > | null;
        } = {};

        shifts?.forEach((item) => {
          const date = dayjs(item.date);
          const dayOfMonth = date.date();
          shiftsData["day" + dayOfMonth] = item;
        });

        dataSubmitArray.forEach((item) => {
          const { isDelete, shiftType, nurseId, note } = item;
          const date = dayjs(item.date);
          if (!date.isSame(yearMonth, "month")) return;

          const dayOfMonth = date.date();
          if (isDelete) {
            shiftsData["day" + dayOfMonth] = null;
          } else {
            shiftsData["day" + dayOfMonth] = {
              shift_type: shiftType,
              nurse_id: nurseId.toString(),
              date: item.date,
              note,
            };
          }
        });

        const newItem = {
          key: id,
          id,
          family_name,
          nurse_code,
          employment_type,
          ...shiftsData,
        };

        if (id === nurseId) {
          result.unshift(newItem);
        } else {
          result.push(newItem);
        }
      });
      return result;
    }

    // prepare for data master(admin)
    const result: any[] = [];

    data?.data?.forEach((item) => {
      const { id, family_name, shifts, nurse_code, employment_type } = item; // id = nurseId
      const dataSubmitArray = preDataSubmit[id]
        ? Object.values(preDataSubmit[id])
        : [];
      const shiftsData: {
        [key: string]: Pick<
          Shift,
          "shift_type" | "date" | "nurse_id" | "note"
        > | null;
      } = {};

      shifts?.forEach((item) => {
        const date = dayjs(item.date);
        const dayOfMonth = date.date();
        shiftsData["day" + dayOfMonth] = item;
      });

      dataSubmitArray.forEach((item) => {
        const { isDelete, shiftType, nurseId, note } = item;
        const date = dayjs(item.date);
        if (!date.isSame(yearMonth, "month")) return;

        const dayOfMonth = date.date();
        if (isDelete) {
          shiftsData["day" + dayOfMonth] = null;
        } else {
          shiftsData["day" + dayOfMonth] = {
            shift_type: shiftType,
            nurse_id: nurseId.toString(),
            date: item.date,
            note,
          };
        }
      });

      const newItem = {
        key: id,
        id,
        family_name,
        nurse_code,
        employment_type,
        ...shiftsData,
      };
      result.push(newItem);
    });
    return result;
  }, [data, preDataSubmit]);

  const locked = data?.shift_lock;
  const disabled = (isStaff && locked) || isViewModel;
  const disabledBtnEditStaff =
    isStaff && dataSource[0]?.id && dataSource[0].id !== nurseId;
  const dataSourcePdf = useMemo(() => {
    const result: any[] = [];

    data?.data?.forEach((item) => {
      const { id, family_name, shifts, nurse_code, employment_type } = item; // id = nurseId()
      const shiftsData: {
        [key: string]: Pick<Shift, "shift_type" | "date" | "nurse_id"> | null;
      } = {};

      shifts?.forEach((item) => {
        const date = dayjs(item.date);
        const dayOfMonth = date.date();
        shiftsData["day" + dayOfMonth] = item;
      });
      const newItem = {
        key: id,
        id,
        family_name,
        nurse_code,
        employment_type,
        ...shiftsData,
      };
      if (id === nurseId && isStaff) {
        result.unshift(newItem);
      } else {
        result.push(newItem);
      }
    });

    return result;
  }, [data]);

  const getNameEmploymentType = (type: string) => {
    return EMPLOYMENT_TYPE_OPTIONS.find((x) => x.value === type)?.label || "";
  };

  const handleSetPrePrint = (isBlur: boolean) => {
    setPrinting(true);
    setIsBlurModePrint(isBlur);
    setTimeout(() => {
      handlePrint();
    }, 100);
  };

  const getColumns = (isBlur?: boolean) => {
    return [
      {
        title: <div className="px-4">介護スタッフ</div>,
        dataIndex: "family_name",
        key: "family_name",
        width: 160,
        render: (_: string, record: any) => {
          return (
            <div className="pl-4 pr-2 min-h-[50px] flex flex-col justify-center">
              <p className="whitespace-nowrap">
                {record.nurse_code}
                <span className="font-bold whitespace-nowrap">
                  【{getNameEmploymentType(record.employment_type)}】
                </span>
              </p>
              <p className=" whitespace-nowrap">{record.family_name}</p>
            </div>
          );
        },
        fixed: "left",
        onCell: (record) => {
          return {
            className:
              disabled || (isStaff && nurseId !== record.id) ? "disabled" : "",
          };
        },
      },

      ...daysArray.map((item, indexCol) => {
        return {
          title: (
            <div className="">
              <div className="h-6 text-center border-b border-[#010a021f]">
                {item.dayOfMonth}
              </div>
              <div className="h-6 text-center">{item.dayOfWeek}</div>
            </div>
          ),
          dataIndex: "day" + (indexCol + 1),
          key: indexCol,
          width: 28,
          onCell: (record: any) => {
            return {
              className:
                disabled || (isStaff && nurseId !== record.id)
                  ? "disabled"
                  : "",
            };
          },
          render: (value: Shift, record: any, index: number) => {
            return (
              <div
                onMouseDown={() => {
                  setNameNurseSelecting(record.family_name);
                  window.getSelection()?.removeAllRanges(); // Clear the text selection on the page
                }}
                className="absolute top-0 left-0 h-full w-full"
              >
                <ShiftCard
                  disabled={disabled || (isStaff && nurseId !== record.id)}
                  indexCol={indexCol}
                  row={index + 1}
                  nurseId={record.id}
                  date={item.date}
                  shiftType={value?.shift_type}
                  note={value?.note}
                  id={value?.id}
                  isBlur={isBlur}
                />
              </div>
            );
          },
        };
      }),
    ] as TableColumnsType<any>;
  };
  const columns: TableColumnsType<any> = useMemo(getColumns, [
    disabled,
    daysArray,
  ]);

  const columnsPdf: TableColumnsType<any> = useMemo(
    () => getColumns(isBlurModePrint),
    [disabled, daysArray, isBlurModePrint]
  );

  const refTable = useRef<HTMLDivElement | null>(null);
  const handleChangedSelected = (dataSelected: SelectedItem[]) => {
    if (dataSelected.length) {
      setOpenModal(true);
    }
    setSelectedItems(dataSelected);
  };
  const { removeSelectedCell } = useShiftSelection(
    refTable,
    disabled,
    handleChangedSelected
  );

  const handleOnCancelModal = () => {
    setOpenModal(false);
    removeSelectedCell();
  };

  const handleSaveTmpDataSubmit = (
    data: {
      dateFormats: string[];
      nurseId: string;
      shiftType: ShiftType;
      isDelete: boolean;
      note?: string | null;
    }[]
  ) => {
    const dataSubmitClone = { ...preDataSubmit };
    data.forEach(({ dateFormats, shiftType, isDelete, nurseId, note }) => {
      const preNurseId = nurseId;
      if (!dataSubmitClone[preNurseId]) dataSubmitClone[preNurseId] = {};
      const dataNew = dateFormats.reduce((accumulator, currentValue) => {
        return {
          ...accumulator,
          [currentValue]: {
            nurseId: preNurseId,
            date: currentValue,
            shiftType: shiftType,
            note,
            isDelete,
          },
        };
      }, {});

      if (preNurseId !== preNurseId && !isDelete) {
        const dataEffect = Object.values(dataNew).reduce(
          (acc: any, cur: any) => {
            return {
              ...acc,
              [cur.date]: {
                nurseId: preNurseId,
                date: cur.date,
                shiftType: shiftType,
                isDelete: true,
              },
            };
          },
          {}
        ) as any;
        dataSubmitClone[preNurseId] = {
          ...dataSubmitClone[preNurseId],
          ...dataEffect,
        };
      }
      dataSubmitClone[preNurseId] = {
        ...dataSubmitClone[preNurseId],
        ...dataNew,
      };
    });
    setPreDataSubmit(dataSubmitClone);
  };

  const handleSubmit = async () => {
    // submit save at BE

    try {
      if (isEmpty(preDataSubmit)) {
        await refetch();
        return setIsViewModel(true);
      }

      setIsSubmitting(true);
      const data: any = [];
      Object.keys(preDataSubmit).map((key) => {
        const result: { [key: string]: any } = {};
        const nurse_id = key;
        const dataItem = preDataSubmit[key];

        Object.values(dataItem).forEach((item) => {
          const shift_type = item.shiftType;
          const note = item.note;
          const isDelete = item.isDelete;
          if (isDelete) {
            if (!result["delete"])
              result["delete"] = {
                dates: [],
                date_deleted: [],
                nurse_id,
                shift_type: SHIFT_TYPES.day_off.value,
              };
            return result["delete"].date_deleted.push(item.date);
          }
          const keyResult = note + "_" + shift_type;
          if (!result[keyResult] && !isDelete)
            result[keyResult] = { dates: [] };
          result[keyResult].nurse_id = nurse_id;
          result[keyResult].shift_type = shift_type;
          result[keyResult].note = note;
          result[keyResult].dates.push(item.date);
        });
        data.push(...Object.values(result));
      });
      const res = (await api.createOrUpdate({
        data,
        year_month: yearMonth,
      })) as any;
      await refetch();
      setPreDataSubmit({});
      toast.success(res.success);
    } catch (e) {
      toast.error("保存が失敗しました。");
    } finally {
      setIsSubmitting(false);
    }
    setIsViewModel(!isViewModel);
  };

  const getTextBtn = () => {
    if (isStaff) return disabled ? "自分の予定を編集" : "保存";
    return disabled ? "編集" : "保存";
  };

  const isBreakBtnAndStatusStaff =
    isStaff &&
    (windowWidth <= 450 ||
      (!locked && windowWidth <= 580) ||
      (!locked && !disabled && windowWidth <= 665));

  const getMaxHeightTable = () => {
    const heightOfTable =
      window.innerHeight - (refTable.current?.offsetTop || 0);
    if (isStaff) {
      if (isBreakBtnAndStatusStaff) {
        return "calc(100vh - 312px)";
      }
      if (windowWidth < 560 && disabled) return "calc(100vh - 282px)";
      if (windowWidth < 560) return "calc(100vh - 312px)";
      if (windowWidth < 1024) return "calc(100vh - 282px)";
      return `${heightOfTable - 140}px`;
    }
    return `${heightOfTable - 135}px`;
  };

  const yearMonthEra = useMemo(
    () => moment(yearMonth).format(DATE_ERA_FORMAT_YEAR_MONTH),
    [yearMonth]
  );

  return (
    <div>
      <div
        className={`page-title ${
          isStaff
            ? `bg-white !m-0 py-[11px] px-6 font-bold ${
                !isMobile && !isTablet && "!mb-4"
              }`
            : ""
        }`}
      >
        シフト管理　休日・出勤申請
      </div>

      <div
        className={`page-container ${
          isStaff ? (isTablet || isMobile ? "m-0" : "mt-3 mx-6") : ""
        }`}
      >
        <div className="w-full bg-white">
          <div
            className={
              isBreakBtnAndStatusStaff ? "" : "flex justify-between mt-1"
            }
          >
            <div className="flex">
              <DateChanger
                picker="month"
                type="months"
                format={MONTH_FORMAT}
                className="!border-b-0 w-min !p-0"
                onChange={(date) => {
                  setYearMonth(date.format(MONTH_FORMAT));
                }}
              />

              {locked !== undefined &&
                (windowWidth < 450 || windowWidth >= 450) && (
                  <Flex alignItems="center">
                    <div
                      className={`${
                        locked ? "bg-red-600" : "bg-green-500"
                      } w-4 h-4 rounded-[100%] mr-1`}
                    ></div>
                    <p>{locked ? "ロック中" : "解除中"}</p>
                  </Flex>
                )}
            </div>
            <Flex gap="12px">
              <Popover
                trigger={"click"}
                placement="left"
                title={"表示パターン"}
                content={
                  <div className="flex flex-col gap-3">
                    <ButtonSolid
                      className="!min-w-[102px]"
                      onClick={() => handleSetPrePrint(false)}
                      isDisabled={isLoading}
                      isLoading={printing && !isBlurModePrint}
                    >
                      通常表示（現状のまま）
                    </ButtonSolid>

                    <ButtonSolid
                      className="!min-w-[102px]"
                      onClick={() => handleSetPrePrint(true)}
                      isDisabled={isLoading}
                      isLoading={printing && isBlurModePrint}
                    >
                      背景色を薄くする
                    </ButtonSolid>
                  </div>
                }
              >
                <ButtonSolid className="!min-w-[102px]" isDisabled={isLoading}>
                  出力
                </ButtonSolid>
              </Popover>

              {!disabled && (
                <ButtonOutline
                  onClick={() => {
                    setPreDataSubmit({});
                    setIsViewModel(true);
                  }}
                >
                  キャンセル
                </ButtonOutline>
              )}
              {(isAdmin || (!locked && locked != undefined)) && (
                <ButtonSolid
                  className="!min-w-[102px]"
                  isLoading={isSubmitting}
                  onClick={() => {
                    if (disabled) return setIsViewModel(false);
                    handleSubmit();
                  }}
                  isDisabled={disabledBtnEditStaff}
                >
                  {getTextBtn()}
                </ButtonSolid>
              )}
              {isAdmin && (
                <ButtonSolid
                  className="!min-w-[102px]"
                  onClick={() => {
                    handleSubmitUpdateStatusLocked();
                  }}
                  isLoading={updatingLocked || isLoading}
                >
                  {locked ? "解除" : "ロック"}
                </ButtonSolid>
              )}
            </Flex>
          </div>
          <TableStyled
            loading={isFetching || isLoading}
            ref={refTable}
            tableLayout="fixed"
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            className="!rounded-none mt-3"
            bordered
            rowClassName={() => "border-b !p-0"}
            scroll={
              dataSource.length
                ? {
                    x: "max-content",
                    ...{
                      ...(dataSource.length > 1
                        ? { y: getMaxHeightTable() }
                        : {}),
                    },
                  }
                : { x: "max-content" }
            }
          />
          {/* for print pdt */}
          <div className="w-[297mm] px-[6mm]" ref={ref} hidden>
            <div className="mb-1 text-base w-full text-center">
              {yearMonthEra}
            </div>
            <TablePDF
              dataSource={dataSourcePdf}
              columns={columnsPdf}
              pagination={false}
              className="!rounded-none"
              bordered
              rowClassName={() => "border-b !p-0  break-inside"}
            />
          </div>
          {/*end */}
          <MolalSchedule
            open={openModal}
            onCancel={handleOnCancelModal}
            selectedItems={selectedItems}
            handleSaveTmpDataSubmit={handleSaveTmpDataSubmit}
            isStaff={isStaff}
            nameNurseSelecting={nameNurseSelecting}
            listShiftManagement={data?.data}
          />
        </div>
      </div>
    </div>
  );
};

export default ManageShiftStaff;
